/**
 * Which days this organisation operates.
 *
 * *** A CEILING OVER EACH MENTOR'S OWN HOURS ***
 * A day is bookable only when the organisation operates on it AND the mentor
 * offers it. Closing a day here closes it for everyone, whatever their
 * individual working hours say.
 *
 * *** CLOSING A DAY NEVER CANCELS A BOOKING ***
 * Sessions already on the calendar go ahead exactly as before — the mentor
 * keeps the reminder and the join flow, the student keeps the session. Only
 * NEW bookings are blocked. That is the right rule, but it must not be a
 * surprise: closing a day with sessions on it shows what is affected first.
 *
 * Saved separately from the rest of Settings, because that confirmation step
 * cannot ride along with a form that saves eleven other fields at once.
 */

import { useState } from 'react'
import { Card, Callout } from '@/components/ui/Page'
import { ConfirmDialog } from '@/components/ui/Modal'
import { CheckboxField } from '@/components/ui/Field'
import { useToast } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import { useOperatingDayImpact, useUpdateSettings } from '@/lib/queries'
import type { TenantSettings } from '@/types/api'
import styles from './SettingsPage.module.css'

/** 0 = Monday .. 6 = Sunday, matching the server. */
const DAYS = [
  { key: 'operates_monday', label: 'Monday', weekday: 0 },
  { key: 'operates_tuesday', label: 'Tuesday', weekday: 1 },
  { key: 'operates_wednesday', label: 'Wednesday', weekday: 2 },
  { key: 'operates_thursday', label: 'Thursday', weekday: 3 },
  { key: 'operates_friday', label: 'Friday', weekday: 4 },
  { key: 'operates_saturday', label: 'Saturday', weekday: 5 },
  { key: 'operates_sunday', label: 'Sunday', weekday: 6 },
] as const

type DayKey = (typeof DAYS)[number]['key']

interface Props {
  settings: TenantSettings
}

export function OperatingDaysCard({ settings }: Props) {
  const toast = useToast()
  const updateMutation = useUpdateSettings()

  /** The weekday awaiting confirmation, or null. Drives the impact fetch. */
  const [closing, setClosing] = useState<{
    key: DayKey
    label: string
    weekday: number
  } | null>(null)

  const impact = useOperatingDayImpact(closing?.weekday ?? null)

  const openCount = DAYS.filter((day) => settings[day.key]).length

  const apply = async (key: DayKey, value: boolean) => {
    try {
      await updateMutation.mutateAsync({ [key]: value })
      toast.success(
        value
          ? `${labelFor(key)} is now an operating day.`
          : `${labelFor(key)} is closed for new bookings.`,
      )
    } catch (caught) {
      // The server names the reason — e.g. refusing to close the last open
      // day, which would make the organisation unbookable entirely.
      toast.error(
        caught instanceof ApiError ? caught.message : 'Could not save.',
      )
    }
  }

  const handleToggle = (day: (typeof DAYS)[number], next: boolean) => {
    // Opening a day takes nothing away, so it needs no ceremony.
    if (next) {
      void apply(day.key, true)
      return
    }
    // Closing one might affect booked sessions — check before doing it.
    setClosing({ key: day.key, label: day.label, weekday: day.weekday })
  }

  const confirmClose = async () => {
    if (!closing) return
    const key = closing.key
    setClosing(null)
    await apply(key, false)
  }

  const affected = impact.data?.booking_count ?? 0

  return (
    <>
      <Card
        title="Operating days"
        description="The days your organisation is open. Mentors can only offer hours on these days."
      >
        {openCount === 1 && (
          <Callout tone="warning" title="Only one operating day">
            Closing the last one would leave students unable to book at all, so
            it is not permitted.
          </Callout>
        )}

        <div className={styles.dayGrid}>
          {DAYS.map((day) => (
            <CheckboxField
              key={day.key}
              label={day.label}
              name={day.key}
              checked={settings[day.key]}
              onChange={(event) => handleToggle(day, event.target.checked)}
              disabled={updateMutation.isPending}
            />
          ))}
        </div>

        <p className={styles.hint}>
          Closing a day stops <strong>new</strong> bookings on it. Sessions
          already booked go ahead as normal — mentors and students keep their
          reminders and the join link.
        </p>
      </Card>

      <ConfirmDialog
        open={closing !== null}
        title={`Close ${closing?.label ?? ''} for new bookings?`}
        confirmLabel={`Close ${closing?.label ?? ''}`}
        destructive
        loading={updateMutation.isPending}
        onConfirm={() => void confirmClose()}
        onCancel={() => setClosing(null)}
        message={
          impact.isLoading ? (
            <p>Checking for booked sessions…</p>
          ) : affected === 0 ? (
            <p>
              No sessions are booked on upcoming {closing?.label}s. Students
              will not be able to book this day from now on.
            </p>
          ) : (
            <>
              <p>
                <strong>
                  {affected} session{affected === 1 ? '' : 's'}
                </strong>{' '}
                already booked on upcoming {closing?.label}s{' '}
                <strong>will still go ahead.</strong> Mentors and students keep
                their reminders and the join link.
              </p>
              <p>Only new {closing?.label} bookings are blocked.</p>

              <ul className={styles.impactList}>
                {(impact.data?.bookings ?? []).slice(0, 8).map((booking) => (
                  <li key={booking.booking_id}>
                    {booking.local_date} at {booking.local_time} —{' '}
                    {booking.mentor_name}
                  </li>
                ))}
              </ul>
              {affected > 8 && (
                <p className={styles.muted}>…and {affected - 8} more.</p>
              )}
            </>
          )
        }
      />
    </>
  )
}

function labelFor(key: DayKey): string {
  return DAYS.find((day) => day.key === key)?.label ?? 'That day'
}
